import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: 100%;
    border-left: 1px solid #dbe4ec;
    background: #f6f9fc;
    overflow: hidden;
`;

const PdfHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 34px;
    padding: 0 8px;
    border-bottom: 1px solid #dfe7ef;
    background: #f8fafc;
`;

const HeaderTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #1c3145;
`;

const RevisionBadge = styled.span`
    display: inline-flex;
    align-items: center;
    height: 18px;
    padding: 0 7px;
    border: 1px solid #d6e1ec;
    border-radius: 999px;
    background: #ffffff;
    color: #5a7087;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.01em;
`;

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const PdfControlToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 30px;
    padding: 0 8px;
    border-bottom: 1px solid #dfe7ef;
    background: #f8fafc;
`;

const ToolbarGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const ToolbarDivider = styled.div`
    width: 1px;
    height: 18px;
    background: #d9e3ed;
`;

const ToolbarButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 20px;
    padding: 0 7px;
    border: 1px solid ${(props) => (props.$danger ? "#e18888" : props.$primary ? "#138064" : "#d7e1eb")};
    border-radius: 4px;
    background: ${(props) =>
        props.$danger ? "#d45a5a" : props.$primary ? "#138a6d" : props.$active ? "#eef4fb" : "#ffffff"};
    color: ${(props) => (props.$danger || props.$primary ? "#ffffff" : "#405568")};
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) =>
            props.$danger ? "#c84b4b" : props.$primary ? "#10765d" : props.$active ? "#e5eef8" : "#f3f7fb"};
        border-color: ${(props) => (props.$danger ? "#c84b4b" : props.$primary ? "#10765d" : "#cfdbe7")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const PageInput = styled.input`
    width: 36px;
    height: 20px;
    padding: 0 6px;
    border: 1px solid #d7e1eb;
    border-radius: 4px;
    background: #ffffff;
    color: #24384d;
    font-size: 10px;
    font-weight: 700;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #9ab5d1;
    }
`;

const PageMeta = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #607489;
    font-weight: 600;
`;

const ZoomControlWrap = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const ZoomTrigger = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 72px;
    height: 24px;
    padding: 0 8px;
    border: 1px solid #d7e1eb;
    border-radius: 6px;
    background: #ffffff;
    color: #31475d;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ZoomMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 5;
    min-width: 148px;
    padding: 8px 0;
    border: 1px solid #d7e1eb;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
`;

const ZoomMenuInputRow = styled.div`
    padding: 0 8px 8px 8px;
`;

const ZoomMenuInput = styled.input`
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #b9c7d6;
    border-radius: 6px;
    background: #ffffff;
    color: #24384d;
    font-size: 12px;
    font-weight: 700;

    &:focus {
        outline: none;
        border-color: #9ab5d1;
    }
`;

const ZoomMenuDivider = styled.div`
    height: 1px;
    margin: 6px 0;
    background: #e3eaf2;
`;

const ZoomMenuItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border: 0;
    background: transparent;
    color: ${(props) => (props.$active ? "#0f8f6f" : "#31475d")};
    font-size: 12px;
    font-weight: ${(props) => (props.$active ? 700 : 600)};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f3f7fb;
    }
`;

const PdfViewport = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow: auto;
    background: #e7ecf2;
`;

const PanelBody = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
`;

const PageStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;

const PageShell = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const PageFrame = styled.div`
    width: ${(props) => `${props.$width}px`};
    min-width: 280px;
    border: 1px solid #d6dfeb;
    background: #ffffff;
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.07);
`;

const PageCanvas = styled.canvas`
    display: block;
    width: 100%;
    height: auto;
    background: #ffffff;
`;

const PagePlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 340px;
    color: #8194a8;
    font-size: 11px;
    background: #ffffff;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 100%;
    padding: 32px;
    color: #5a6d82;
    text-align: center;
`;

const PanelOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 12;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    min-height: 0;
    padding: 14px;
    background: rgba(244, 248, 252, 0.72);
    backdrop-filter: blur(2px);
`;

const CompileOverlayCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 220px;
    margin: auto;
    padding: 22px 24px;
    border: 1px solid rgba(215, 225, 235, 0.9);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
    color: #24405a;
    text-align: center;
`;

const OverlaySpinner = styled.div`
    width: 28px;
    height: 28px;
    border: 3px solid #d9e4ee;
    border-top-color: #138a6d;
    border-radius: 50%;
    animation: manuscript-pdf-spin 0.8s linear infinite;

    @keyframes manuscript-pdf-spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const OverlayTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #1f3449;
`;

const OverlayDescription = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #61758a;
`;

const LogPanel = styled.div`
    display: flex;
    flex: 1;
    width: 100%;
    min-height: 0;
    flex-direction: column;
    border: 1px solid #d8e2ec;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
    overflow: hidden;
`;

const LogPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #e2eaf2;
    background: #f8fafc;
`;

const LogPanelHeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const LogPanelTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #1f3449;
`;

const LogPanelSubtitle = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #6a7d92;
`;

const LogPanelActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const LogPanelBody = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px 16px;
    overflow: auto;
    background: #fbfdff;
`;

const LogSummaryRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
`;

const LogSummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid #dce6f0;
    border-radius: 10px;
    background: ${(props) => props.$background || "#ffffff"};
`;

const LogSummaryLabel = styled.div`
    font-size: 10px;
    font-weight: 700;
    color: #667b90;
`;

const LogSummaryValue = styled.div`
    font-size: 16px;
    font-weight: 800;
    color: #203249;
`;

const LogSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LogSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const LogSectionTitle = styled.div`
    font-size: 11px;
    font-weight: 800;
    color: #3f556b;
`;

const LogEntryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LogEntryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid
        ${(props) => (props.$level === "error" ? "#f1c1c1" : props.$level === "warning" ? "#f2dfb2" : "#d8e3ed")};
    border-radius: 10px;
    background: ${(props) =>
        props.$level === "error" ? "#fff7f7" : props.$level === "warning" ? "#fffaf0" : "#ffffff"};
`;

const LogEntryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const LogEntryTitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
`;

const LogEntryMetaRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const LogEntryHeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const LogEntryBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 48px;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: ${(props) =>
        props.$level === "error" ? "#fbe3e3" : props.$level === "warning" ? "#fbefce" : "#eaf2fb"};
    color: ${(props) => (props.$level === "error" ? "#b43a3a" : props.$level === "warning" ? "#9c6a0d" : "#4b647e")};
    font-size: 10px;
    font-weight: 800;
`;

const LogEntryJumpButton = styled(ToolbarButton)`
    white-space: nowrap;
    flex-shrink: 0;
`;

const LogEntryMessage = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #24384d;
    line-height: 1.45;
    word-break: break-word;
`;

const LogEntryLocation = styled.div`
    font-size: 10px;
    font-weight: 700;
    color: #6a7d92;
`;

const LogEntryContent = styled.pre`
    margin: 0;
    padding: 10px 11px;
    border-radius: 8px;
    background: #f5f8fb;
    color: #3c5369;
    font-size: 11px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
`;

const LogRawBox = styled.pre`
    margin: 0;
    padding: 12px 13px;
    border: 1px solid #dbe5ee;
    border-radius: 10px;
    background: #f7fafc;
    color: #344a60;
    font-size: 11px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
`;

const LogEmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 18px;
    border: 1px dashed #d7e1eb;
    border-radius: 10px;
    background: #ffffff;
    color: #6a7d92;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
`;

const LOG_ENTRY_LIMIT = 80;

function ManuscriptPdfPanel({
    latestCompile,
    pdfArtifact,
    pdfRevisionSeq,
    logArtifact,
    compileRevisionSeq,
    compileLogRaw,
    compileLogEntries,
    compileLogLoading,
    compileLogLoadError,
    onCompile,
    compileLoading,
    compileDisabled,
    compileHasLintError,
    compileDisabledReason,
    onLogEntrySelect,
}) {
    const [zoomMode, setZoomMode] = useState("fit-width");
    const [customScale, setCustomScale] = useState(1);
    const [pdfDocument, setPdfDocument] = useState(null);
    const [pdfPreviewError, setPdfPreviewError] = useState("");
    const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
    const [rendering, setRendering] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [zoomInput, setZoomInput] = useState("100");
    const [isZoomMenuOpen, setIsZoomMenuOpen] = useState(false);
    const [isLogPanelOpen, setIsLogPanelOpen] = useState(false);
    const [viewportSize, setViewportSize] = useState({ width: 800, height: 1040 });
    const [basePageSize, setBasePageSize] = useState(null);
    const [pageDimensions, setPageDimensions] = useState({});
    const [renderedPages, setRenderedPages] = useState([1, 2]);

    const viewportRef = useRef(null);
    const canvasRefs = useRef(new Map());
    const pageWrapRefs = useRef(new Map());
    const scrollRafRef = useRef(null);
    const renderTasksRef = useRef(new Map());
    const zoomControlRef = useRef(null);
    const zoomInputRef = useRef(null);

    const compileStatus = latestCompile?.status || "idle";
    const isCompileFailed = compileStatus === "failed";
    const isCompileInProgress = compileLoading || compileStatus === "queued" || compileStatus === "running";
    const compileTooltipMessage = isCompileInProgress
        ? "컴파일 작업이 진행 중입니다"
        : compileHasLintError
          ? "문법 오류가 있습니다. 클릭하면 오류 위치로 이동합니다"
          : compileDisabledReason || "작성중인 문서를 PDF로 변환합니다";
    const parsedLogEntries = Array.isArray(compileLogEntries) ? compileLogEntries : [];
    const hasLogContent =
        !!logArtifact?.file_url || !!compileLogRaw || !!compileLogLoadError || parsedLogEntries.length > 0;
    const logSummary = useMemo(
        () => ({
            all: parsedLogEntries.length,
            errors: parsedLogEntries.filter((entry) => entry.level === "error").length,
            warnings: parsedLogEntries.filter((entry) => entry.level === "warning").length,
            info: parsedLogEntries.filter((entry) => entry.level === "info").length,
        }),
        [parsedLogEntries],
    );
    const emptyStateMessage = useMemo(() => {
        if (pdfPreviewLoading) {
            return "PDF 미리보기를 불러오는 중입니다.";
        }
        if (pdfPreviewError) {
            return pdfPreviewError;
        }
        return "아직 PDF 미리보기가 없습니다. 컴파일을 눌러 생성하세요.";
    }, [pdfPreviewError, pdfPreviewLoading]);

    const resolveScale = useCallback(
        (baseViewport) => {
            if (zoomMode === "custom") return Math.max(0.25, customScale);
            if (zoomMode === "fit-page") {
                return Math.max(
                    0.25,
                    Math.min(
                        (viewportSize.width - 36) / baseViewport.width,
                        (viewportSize.height - 36) / baseViewport.height,
                    ),
                );
            }
            return Math.max(0.25, (viewportSize.width - 36) / baseViewport.width);
        },
        [customScale, viewportSize.height, viewportSize.width, zoomMode],
    );

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setViewportSize({
                width: Math.max(320, Math.floor(entry.contentRect.width)),
                height: Math.max(420, Math.floor(entry.contentRect.height)),
            });
        });
        observer.observe(viewport);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        let loadingTask = null;

        const loadPreview = async () => {
            if (!pdfArtifact?.file_url) {
                setPdfDocument((prev) => {
                    prev?.destroy?.();
                    return null;
                });
                setPageCount(0);
                setCurrentPage(1);
                setPageInput("1");
                setRenderedPages([1, 2]);
                setPageDimensions({});
                setBasePageSize(null);
                setPdfPreviewLoading(false);
                setPdfPreviewError("");
                return;
            }

            setPdfPreviewLoading(true);
            setPdfPreviewError("");
            try {
                const response = await fetch(pdfArtifact.file_url);
                if (!response.ok) throw new Error(`PDF fetch failed: ${response.status}`);
                const arrayBuffer = await response.arrayBuffer();
                loadingTask = getDocument({ data: new Uint8Array(arrayBuffer) });
                const document = await loadingTask.promise;
                if (cancelled) {
                    document.destroy?.();
                    return;
                }
                setPdfDocument((prev) => {
                    prev?.destroy?.();
                    return document;
                });
                setPageCount(document.numPages || 0);
                setCurrentPage(1);
                setPageInput("1");
                setRenderedPages([1, 2]);
                setPageDimensions({});
                setBasePageSize(null);
            } catch (error) {
                if (!cancelled) {
                    setPdfDocument((prev) => {
                        prev?.destroy?.();
                        return null;
                    });
                    setPageCount(0);
                    setCurrentPage(1);
                    setPageInput("1");
                    setBasePageSize(null);
                    setPdfPreviewError("PDF 미리보기를 불러오지 못했습니다.");
                }
            } finally {
                if (!cancelled) setPdfPreviewLoading(false);
            }
        };

        loadPreview();
        return () => {
            cancelled = true;
            loadingTask?.destroy?.();
        };
    }, [pdfArtifact?.file_url]);

    useEffect(() => {
        if (isCompileFailed && hasLogContent) {
            setIsLogPanelOpen(true);
        }
    }, [hasLogContent, isCompileFailed, latestCompile?.id]);

    useEffect(() => {
        if (isCompileInProgress) {
            setIsLogPanelOpen(false);
        }
    }, [isCompileInProgress]);

    useEffect(() => {
        if (!pageCount) return;
        const next = new Set(renderedPages);
        for (let p = Math.max(1, currentPage - 2); p <= Math.min(pageCount, currentPage + 2); p += 1) {
            next.add(p);
        }
        const nextArray = Array.from(next).sort((a, b) => a - b);
        if (
            nextArray.length !== renderedPages.length ||
            nextArray.some((value, index) => value !== renderedPages[index])
        ) {
            setRenderedPages(nextArray);
        }
    }, [currentPage, pageCount, renderedPages]);

    useEffect(() => {
        if (!pdfDocument || !pageCount) return undefined;
        let cancelled = false;

        renderTasksRef.current.forEach((task) => {
            try {
                task?.cancel?.();
            } catch (error) {
                // noop
            }
        });
        renderTasksRef.current.clear();

        const render = async () => {
            setRendering(true);
            try {
                for (const pageNumber of renderedPages) {
                    if (cancelled) return;
                    const canvas = canvasRefs.current.get(pageNumber);
                    if (!canvas) continue;
                    const page = await pdfDocument.getPage(pageNumber);
                    if (cancelled) return;

                    const baseViewport = page.getViewport({ scale: 1 });
                    if (pageNumber === 1) {
                        setBasePageSize((prev) => {
                            const nextWidth = Math.round(baseViewport.width);
                            const nextHeight = Math.round(baseViewport.height);
                            if (prev?.width === nextWidth && prev?.height === nextHeight) {
                                return prev;
                            }
                            return { width: nextWidth, height: nextHeight };
                        });
                    }
                    const scale = resolveScale(baseViewport);
                    const viewport = page.getViewport({ scale });
                    const ratio = window.devicePixelRatio || 1;
                    const context = canvas.getContext("2d", { alpha: false });

                    canvas.width = Math.floor(viewport.width * ratio);
                    canvas.height = Math.floor(viewport.height * ratio);
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;
                    context.setTransform(ratio, 0, 0, ratio, 0, 0);

                    const renderTask = page.render({ canvasContext: context, viewport });
                    renderTasksRef.current.set(pageNumber, renderTask);
                    try {
                        await renderTask.promise;
                    } catch (error) {
                        if (error?.name !== "RenderingCancelledException") throw error;
                    } finally {
                        if (renderTasksRef.current.get(pageNumber) === renderTask) {
                            renderTasksRef.current.delete(pageNumber);
                        }
                    }
                    if (cancelled) return;

                    setPageDimensions((prev) => {
                        const old = prev[pageNumber];
                        const nextWidth = Math.max(280, Math.round(viewport.width));
                        const nextHeight = Math.max(360, Math.round(viewport.height));
                        if (old && old.width === nextWidth && old.height === nextHeight) return prev;
                        return { ...prev, [pageNumber]: { width: nextWidth, height: nextHeight } };
                    });
                }
            } finally {
                if (!cancelled) setRendering(false);
            }
        };

        render();
        return () => {
            cancelled = true;
            renderTasksRef.current.forEach((task) => {
                try {
                    task?.cancel?.();
                } catch (error) {
                    // noop
                }
            });
            renderTasksRef.current.clear();
        };
    }, [pageCount, pdfDocument, renderedPages, resolveScale]);

    const syncCurrentPageFromScroll = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || !pageCount) return;
        const viewportRect = viewport.getBoundingClientRect();
        const centerY = viewportRect.top + viewportRect.height / 2;
        let bestPage = currentPage;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (const [page, element] of pageWrapRefs.current.entries()) {
            if (!element) continue;
            const rect = element.getBoundingClientRect();
            const distance = Math.abs(rect.top + rect.height / 2 - centerY);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestPage = page;
            }
        }
        if (bestPage !== currentPage) {
            setCurrentPage(bestPage);
            setPageInput(String(bestPage));
        }
    }, [currentPage, pageCount]);

    const handlePdfViewportScroll = useCallback(() => {
        if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = requestAnimationFrame(syncCurrentPageFromScroll);
    }, [syncCurrentPageFromScroll]);

    useEffect(() => {
        return () => {
            if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
        };
    }, []);

    const scrollToPage = useCallback(
        (page) => {
            const nextPage = Math.min(Math.max(1, page), pageCount || 1);
            setCurrentPage(nextPage);
            setPageInput(String(nextPage));
            const node = pageWrapRefs.current.get(nextPage);
            if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
        },
        [pageCount],
    );

    const handlePageInputCommit = useCallback(() => {
        const parsed = Number.parseInt(pageInput, 10);
        if (!Number.isFinite(parsed) || parsed < 1 || parsed > (pageCount || 1)) {
            setPageInput(String(currentPage));
            return;
        }
        scrollToPage(parsed);
    }, [currentPage, pageCount, pageInput, scrollToPage]);

    const handlePdfViewportWheel = useCallback((event) => {
        if (!(event.ctrlKey || event.metaKey)) return;
        event.preventDefault();
        setZoomMode("custom");
        setCustomScale((prev) => {
            const delta = event.deltaY > 0 ? -0.08 : 0.08;
            return Math.min(4, Math.max(0.25, Number((prev + delta).toFixed(2))));
        });
    }, []);

    const stepZoom = useCallback(
        (delta) => {
            const nextScale = basePageSize ? resolveScale(basePageSize) + delta : customScale + delta;
            setZoomMode("custom");
            setCustomScale(Number(Math.min(4, Math.max(0.25, nextScale)).toFixed(2)));
            setIsZoomMenuOpen(false);
        },
        [basePageSize, customScale, resolveScale],
    );

    const zoomOut = useCallback(() => {
        stepZoom(-0.1);
    }, [stepZoom]);

    const zoomIn = useCallback(() => {
        stepZoom(0.1);
    }, [stepZoom]);

    const normalizeZoomInputValue = useCallback((value) => {
        const normalized = String(value || "").replace(/[^\d.]/g, "");
        if (!normalized) return null;
        const parsed = Number.parseFloat(normalized);
        if (!Number.isFinite(parsed)) return null;
        if (parsed < 25 || parsed > 400) return null;
        return parsed;
    }, []);

    const applyCustomZoomPercent = useCallback(
        (percent) => {
            const normalized = normalizeZoomInputValue(percent);
            if (normalized == null) {
                return false;
            }
            setZoomMode("custom");
            setCustomScale(Number((normalized / 100).toFixed(2)));
            setZoomInput(String(Math.round(normalized)));
            setIsZoomMenuOpen(false);
            return true;
        },
        [normalizeZoomInputValue],
    );

    const handleZoomInputCommit = useCallback(() => {
        applyCustomZoomPercent(zoomInput);
    }, [applyCustomZoomPercent, zoomInput]);

    const handleDownloadPdf = useCallback(async () => {
        if (!pdfArtifact?.file_url) {
            return;
        }

        try {
            const response = await fetch(pdfArtifact.file_url);
            if (!response.ok) {
                throw new Error(`PDF download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const pathName = new URL(pdfArtifact.file_url, window.location.href).pathname;
            const fallbackName = pathName.split("/").pop() || "manuscript.pdf";

            link.href = blobUrl;
            link.download = fallbackName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (_error) {
            window.open(pdfArtifact.file_url, "_blank", "noopener,noreferrer");
        }
    }, [pdfArtifact?.file_url]);

    const handleDownloadLog = useCallback(async () => {
        if (!logArtifact?.file_url) {
            return;
        }

        try {
            const response = await fetch(logArtifact.file_url);
            if (!response.ok) {
                throw new Error(`LOG download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const pathName = new URL(logArtifact.file_url, window.location.href).pathname;
            const fallbackName = pathName.split("/").pop() || "compile.log.txt";

            link.href = blobUrl;
            link.download = fallbackName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (_error) {
            window.open(logArtifact.file_url, "_blank", "noopener,noreferrer");
        }
    }, [logArtifact?.file_url]);

    const activeZoomPercent = Math.round(
        (zoomMode === "custom" ? customScale : basePageSize ? resolveScale(basePageSize) : customScale) * 100,
    );

    useEffect(() => {
        setRenderedPages((prev) => {
            const next = new Set(prev);
            for (let p = Math.max(1, currentPage - 2); p <= Math.min(pageCount, currentPage + 2); p += 1) {
                next.add(p);
            }
            return Array.from(next).sort((a, b) => a - b);
        });
    }, [currentPage, pageCount, zoomMode, customScale, viewportSize.width, viewportSize.height]);

    useEffect(() => {
        if (!isZoomMenuOpen) {
            setZoomInput(String(activeZoomPercent));
        }
    }, [activeZoomPercent, isZoomMenuOpen]);

    useEffect(() => {
        if (!isZoomMenuOpen) return undefined;

        const handleClickOutside = (event) => {
            if (!zoomControlRef.current?.contains(event.target)) {
                setIsZoomMenuOpen(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsZoomMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isZoomMenuOpen]);

    useEffect(() => {
        if (!isZoomMenuOpen) return;
        window.requestAnimationFrame(() => {
            zoomInputRef.current?.focus();
            zoomInputRef.current?.select();
        });
    }, [isZoomMenuOpen]);

    useEffect(() => {
        return () => {
            setPdfDocument((prev) => {
                prev?.destroy?.();
                return null;
            });
            renderTasksRef.current.forEach((task) => {
                try {
                    task?.cancel?.();
                } catch (error) {
                    // noop
                }
            });
            renderTasksRef.current.clear();
        };
    }, []);

    const zoomPresetItems = [50, 75, 100, 125, 150];

    return (
        <Panel>
            <PdfHeader>
                <HeaderTitle>
                    <Icon name={"pdf"} size={"14px"} color={"#0f8f6f"} />
                    PDF
                    {pdfRevisionSeq != null ? <RevisionBadge>{`r${pdfRevisionSeq}`}</RevisionBadge> : null}
                </HeaderTitle>
                <HeaderActions>
                    <Tooltip message={compileTooltipMessage}>
                        <ToolbarButton
                            type="button"
                            onClick={onCompile}
                            disabled={compileDisabled || isCompileInProgress}
                            $primary
                            $danger={compileHasLintError && !compileDisabled}
                        >
                            <Icon name={"play"} size={"10px"} color={"#ffffff"} />
                            {isCompileInProgress ? "대기중" : "컴파일"}
                        </ToolbarButton>
                    </Tooltip>
                    <Tooltip message="컴파일 로그">
                        <ToolbarButton
                            type="button"
                            onClick={() => setIsLogPanelOpen(true)}
                            disabled={!hasLogContent}
                            $danger={isCompileFailed}
                        >
                            로그
                        </ToolbarButton>
                    </Tooltip>
                    <Tooltip message="다운로드">
                        {pdfArtifact?.file_url ? (
                            <ToolbarButton type="button" onClick={handleDownloadPdf}>
                                <Icon name={"download"} size={"12px"} color={"#6a7f97"} />
                            </ToolbarButton>
                        ) : (
                            <ToolbarButton type="button" disabled>
                                <Icon name={"download"} size={"12px"} color={"#6a7f97"} />
                            </ToolbarButton>
                        )}
                    </Tooltip>
                </HeaderActions>
            </PdfHeader>

            <PanelBody>
                <PdfControlToolbar>
                    <ToolbarGroup>
                        <ToolbarButton
                            type="button"
                            onClick={() => scrollToPage(currentPage - 1)}
                            disabled={!pageCount || currentPage <= 1}
                        >
                            <Icon name={"arrowLeft"} size={"10px"} color={"#405568"} />
                        </ToolbarButton>
                        <PageInput
                            value={pageInput}
                            onChange={(event) => setPageInput(event.target.value)}
                            onBlur={handlePageInputCommit}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    handlePageInputCommit();
                                }
                            }}
                        />
                        <PageMeta>
                            <span>/</span>
                            <span>{pageCount || 0}</span>
                        </PageMeta>
                        <ToolbarButton
                            type="button"
                            onClick={() => scrollToPage(currentPage + 1)}
                            disabled={!pageCount || currentPage >= pageCount}
                        >
                            <Icon name={"arrowRight"} size={"10px"} color={"#405568"} />
                        </ToolbarButton>
                    </ToolbarGroup>
                    <ToolbarDivider />
                    <ToolbarGroup>
                        <ZoomControlWrap ref={zoomControlRef}>
                            <ZoomTrigger
                                type="button"
                                disabled={!pdfDocument}
                                onClick={() => {
                                    setZoomInput(String(activeZoomPercent));
                                    setIsZoomMenuOpen((prev) => !prev);
                                }}
                            >
                                <span>{activeZoomPercent}%</span>
                                <Icon name={"arrowDown"} size={"10px"} color={"#405568"} />
                            </ZoomTrigger>
                            {isZoomMenuOpen ? (
                                <ZoomMenu>
                                    <ZoomMenuInputRow>
                                        <ZoomMenuInput
                                            ref={zoomInputRef}
                                            value={zoomInput}
                                            onChange={(event) => setZoomInput(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    handleZoomInputCommit();
                                                }
                                            }}
                                        />
                                    </ZoomMenuInputRow>
                                    <ZoomMenuItem type="button" onClick={zoomIn}>
                                        확대(+10%)
                                    </ZoomMenuItem>
                                    <ZoomMenuItem type="button" onClick={zoomOut}>
                                        축소(-10%)
                                    </ZoomMenuItem>
                                    <ZoomMenuDivider />
                                    <ZoomMenuItem
                                        type="button"
                                        $active={zoomMode === "fit-width"}
                                        onClick={() => {
                                            setZoomMode("fit-width");
                                            setIsZoomMenuOpen(false);
                                        }}
                                    >
                                        너비 맞춤
                                    </ZoomMenuItem>
                                    <ZoomMenuItem
                                        type="button"
                                        $active={zoomMode === "fit-page"}
                                        onClick={() => {
                                            setZoomMode("fit-page");
                                            setIsZoomMenuOpen(false);
                                        }}
                                    >
                                        페이지 맞춤
                                    </ZoomMenuItem>
                                    <ZoomMenuDivider />
                                    {zoomPresetItems.map((item) => (
                                        <ZoomMenuItem
                                            key={item}
                                            type="button"
                                            $active={zoomMode === "custom" && activeZoomPercent === item}
                                            onClick={() => applyCustomZoomPercent(item)}
                                        >
                                            {item}%로 보기
                                        </ZoomMenuItem>
                                    ))}
                                </ZoomMenu>
                            ) : null}
                        </ZoomControlWrap>
                    </ToolbarGroup>
                </PdfControlToolbar>

                <PdfViewport ref={viewportRef} onScroll={handlePdfViewportScroll} onWheel={handlePdfViewportWheel}>
                    {pdfDocument ? (
                        <PageStack>
                            {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => {
                                const dim = pageDimensions[pageNumber];
                                const shouldRenderCanvas = renderedPages.includes(pageNumber);
                                return (
                                    <PageShell
                                        key={pageNumber}
                                        ref={(node) => {
                                            if (node) pageWrapRefs.current.set(pageNumber, node);
                                            else pageWrapRefs.current.delete(pageNumber);
                                        }}
                                    >
                                        <PageFrame $width={dim?.width || 760}>
                                            {shouldRenderCanvas ? (
                                                <PageCanvas
                                                    ref={(node) => {
                                                        if (node) canvasRefs.current.set(pageNumber, node);
                                                        else canvasRefs.current.delete(pageNumber);
                                                    }}
                                                />
                                            ) : (
                                                <PagePlaceholder>Page {pageNumber}</PagePlaceholder>
                                            )}
                                        </PageFrame>
                                    </PageShell>
                                );
                            })}
                        </PageStack>
                    ) : (
                        <EmptyState>
                            <Icon name={"pdf"} size={"26px"} color={"#8ba0b5"} />
                            <div>{emptyStateMessage}</div>
                        </EmptyState>
                    )}
                    {rendering ? (
                        <EmptyState style={{ position: "absolute", inset: 0 }}>PDF 렌더링 중...</EmptyState>
                    ) : null}
                </PdfViewport>

                {isCompileInProgress ? (
                    <PanelOverlay>
                        <CompileOverlayCard>
                            <OverlaySpinner />
                            <OverlayTitle>컴파일 중</OverlayTitle>
                            <OverlayDescription>
                                PDF를 생성하고 있습니다. 완료되면 자동으로 반영됩니다.
                            </OverlayDescription>
                        </CompileOverlayCard>
                    </PanelOverlay>
                ) : null}

                {isLogPanelOpen ? (
                    <PanelOverlay>
                        <LogPanel role="dialog" aria-modal="true" aria-label="컴파일 로그">
                            <LogPanelHeader>
                                <LogPanelHeaderInfo>
                                    <LogPanelTitle>
                                        컴파일 로그
                                        {compileRevisionSeq != null ? (
                                            <RevisionBadge>{`r${compileRevisionSeq}`}</RevisionBadge>
                                        ) : null}
                                    </LogPanelTitle>
                                    <LogPanelSubtitle>
                                        {latestCompile?.finished_at
                                            ? `최근 컴파일 결과를 확인합니다.`
                                            : "최근 컴파일 로그를 확인합니다."}
                                    </LogPanelSubtitle>
                                </LogPanelHeaderInfo>
                                <LogPanelActions>
                                    <ToolbarButton type="button" onClick={() => setIsLogPanelOpen(false)}>
                                        닫기
                                    </ToolbarButton>
                                </LogPanelActions>
                            </LogPanelHeader>
                            <LogPanelBody>
                                {compileLogLoading ? (
                                    <LogEmptyState>로그를 불러오는 중입니다.</LogEmptyState>
                                ) : compileLogLoadError ? (
                                    <LogEmptyState>{compileLogLoadError}</LogEmptyState>
                                ) : (
                                    <>
                                        <LogSummaryRow>
                                            <LogSummaryCard>
                                                <LogSummaryLabel>전체</LogSummaryLabel>
                                                <LogSummaryValue>{logSummary.all}</LogSummaryValue>
                                            </LogSummaryCard>
                                            <LogSummaryCard $background="#fff7f7">
                                                <LogSummaryLabel>에러</LogSummaryLabel>
                                                <LogSummaryValue>{logSummary.errors}</LogSummaryValue>
                                            </LogSummaryCard>
                                            <LogSummaryCard $background="#fffaf0">
                                                <LogSummaryLabel>경고</LogSummaryLabel>
                                                <LogSummaryValue>{logSummary.warnings}</LogSummaryValue>
                                            </LogSummaryCard>
                                            <LogSummaryCard $background="#f3f8fd">
                                                <LogSummaryLabel>정보</LogSummaryLabel>
                                                <LogSummaryValue>{logSummary.info}</LogSummaryValue>
                                            </LogSummaryCard>
                                        </LogSummaryRow>

                                        <LogSection>
                                            <LogSectionTitle>파싱된 로그 항목</LogSectionTitle>
                                            {parsedLogEntries.length ? (
                                                <LogEntryList>
                                                    {parsedLogEntries.slice(0, LOG_ENTRY_LIMIT).map((entry) => (
                                                        <LogEntryCard key={entry.id} $level={entry.level}>
                                                            <LogEntryHeader>
                                                                <LogEntryTitleBlock>
                                                                    <LogEntryMetaRow>
                                                                        <LogEntryBadge $level={entry.level}>
                                                                            {entry.level === "error"
                                                                                ? "에러"
                                                                                : entry.level === "warning"
                                                                                  ? "경고"
                                                                                  : "정보"}
                                                                        </LogEntryBadge>
                                                                        <LogEntryHeaderActions>
                                                                            <LogEntryJumpButton
                                                                                type="button"
                                                                                disabled={!entry.file || !entry.line}
                                                                                onClick={() =>
                                                                                    onLogEntrySelect?.(entry)
                                                                                }
                                                                            >
                                                                                에디터로 이동
                                                                            </LogEntryJumpButton>
                                                                        </LogEntryHeaderActions>
                                                                    </LogEntryMetaRow>
                                                                    <LogEntryMessage>{entry.message}</LogEntryMessage>
                                                                    {entry.file || entry.line ? (
                                                                        <LogEntryLocation>
                                                                            {entry.file || "파일 정보 없음"}
                                                                            {entry.line ? ` · ${entry.line}행` : ""}
                                                                        </LogEntryLocation>
                                                                    ) : null}
                                                                </LogEntryTitleBlock>
                                                            </LogEntryHeader>
                                                            {entry.raw && entry.raw !== entry.message ? (
                                                                <LogEntryContent>{entry.raw}</LogEntryContent>
                                                            ) : null}
                                                        </LogEntryCard>
                                                    ))}
                                                    {parsedLogEntries.length > LOG_ENTRY_LIMIT ? (
                                                        <LogEmptyState>
                                                            파싱된 로그가 많아 상위 {LOG_ENTRY_LIMIT}개만 표시합니다.
                                                            전체 내용은 아래 원본 로그에서 확인할 수 있습니다.
                                                        </LogEmptyState>
                                                    ) : null}
                                                </LogEntryList>
                                            ) : (
                                                <LogEmptyState>파싱 가능한 에러/경고 항목이 없습니다.</LogEmptyState>
                                            )}
                                        </LogSection>

                                        <LogSection>
                                            <LogSectionHeader>
                                                <LogSectionTitle>전체 로그</LogSectionTitle>
                                                <ToolbarButton
                                                    type="button"
                                                    onClick={handleDownloadLog}
                                                    disabled={!logArtifact?.file_url}
                                                >
                                                    로그 다운로드
                                                </ToolbarButton>
                                            </LogSectionHeader>
                                            {compileLogRaw ? (
                                                <LogRawBox>{compileLogRaw}</LogRawBox>
                                            ) : (
                                                <LogEmptyState>표시할 로그가 없습니다.</LogEmptyState>
                                            )}
                                        </LogSection>
                                    </>
                                )}
                            </LogPanelBody>
                        </LogPanel>
                    </PanelOverlay>
                ) : null}
            </PanelBody>
        </Panel>
    );
}

export default ManuscriptPdfPanel;
